import { useState, useId, useEffect, useRef } from "react";
import logo from "./logo.svg";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Phone,
  Loader2,
  CheckCircle,
  MessageCircle,
  ThumbsUp,
  Calendar,
  Star,
} from "lucide-react";
import { useAction, useMutation } from "convex/react";
import { api } from "../../../convex/api";

import callIcon from "./call-icon.svg";
import recruiterHead from "./recruiter_head.svg";
import recruiterBorder from "./recruiter_border.svg";
import schedulingHead from "./scheduling_head.svg";
import schedulingBorder from "./scheduling_border.svg";
import payrollHead from "./payroll_head.svg";
import payrollBorder from "./payroll_border.svg";

// Custom hook for animated conversation counts
function useAnimatedCount() {
  const [count, setCount] = useState(() => Math.floor(Math.random() * 5) + 2); // 2-6 random initial count
  const [isPulsing, setIsPulsing] = useState(false);
  const timeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const updateCount = () => {
      const change = Math.random() > 0.5 ? 1 : -1;
      const newCount = Math.max(2, Math.min(30, count + change));

      if (newCount !== count) {
        setCount(newCount);
        setIsPulsing(true);

        // Reset pulse after animation
        setTimeout(() => setIsPulsing(false), 600);
      }

      // Schedule next update (4-10 seconds)
      const nextUpdate = Math.random() * 6000 + 4000; // 4000-10000ms
      timeoutRef.current = window.setTimeout(updateCount, nextUpdate);
    };

    // Start the first update
    timeoutRef.current = window.setTimeout(
      updateCount,
      Math.random() * 6000 + 4000,
    );

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [count]);

  return { count, isPulsing };
}

export function AgentForm() {
  const firstNameId = useId();
  const lastNameId = useId();
  const phoneId = useId();
  const emailId = useId();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isCalling, setIsCalling] = useState(false);
  const [callSuccess, setCallSuccess] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedAgentType, setSelectedAgentType] = useState<
    "recruiter" | "scheduling" | "payroll" | null
  >(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Animated conversation counts for each agent
  const recruiterCount = useAnimatedCount();
  const schedulingCount = useAnimatedCount();
  const payrollCount = useAnimatedCount();

  // Use VAPI for making calls instead of Twilio/Pipecat
  const makeCall = useAction(api.vapiActions.makeVapiCall);
  const validatePassword = useMutation(api.auth.validatePassword);

  // Function to get or create session ID
  const getOrCreateSession = async () => {
    if (sessionId) return sessionId;

    try {
      const result = await validatePassword({ password: "cali" });
      if (result.isValid && result.sessionId) {
        setSessionId(result.sessionId);
        return result.sessionId;
      }
      throw new Error("Failed to create session");
    } catch (error) {
      console.error("Error creating session:", error);
      throw error;
    }
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, "");

    // Limit to 10 digits for US phone numbers
    const limitedNumber = phoneNumber.slice(0, 10);

    // Format as (xxx) xxx-xxxx
    if (limitedNumber.length === 0) return "";
    if (limitedNumber.length <= 3) return `(${limitedNumber}`;
    if (limitedNumber.length <= 6)
      return `(${limitedNumber.slice(0, 3)}) ${limitedNumber.slice(3)}`;
    return `(${limitedNumber.slice(0, 3)}) ${limitedNumber.slice(3, 6)}-${limitedNumber.slice(6)}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const formattedPhone = formatPhoneNumber(value);
      setFormData({
        ...formData,
        phone: formattedPhone,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    setTimeout(async () => {
      setIsLoading(false);
      setIsCalling(true);

      try {
        // Prepare the phone number - ensure it's in E.164 format
        let phoneNumber = formData.phone.replace(/\D/g, "");
        if (phoneNumber && !phoneNumber.startsWith("+")) {
          // Assume US number if no country code
          if (!phoneNumber.startsWith("1") && phoneNumber.length === 10) {
            phoneNumber = "1" + phoneNumber;
          }
          phoneNumber = "+" + phoneNumber;
        }

        // Get or create session ID
        const currentSessionId = await getOrCreateSession();

        if (!selectedAgentType) {
          throw new Error("No agent type selected");
        }

        // Make the call via Convex with retry logic
        let result;
        let lastError;
        
        for (let attempt = 1; attempt <= 2; attempt++) {
          try {
            result = await makeCall({
              sessionId: currentSessionId,
              agentType: selectedAgentType,
              phoneNumber: phoneNumber || undefined,
              bodyData: {
                firstName: formData.firstName || undefined,
                lastName: formData.lastName || undefined,
                email: formData.email || undefined,
              },
            });
            break; // Success, exit retry loop
          } catch (error) {
            lastError = error;
            console.error(`VAPI call attempt ${attempt} failed:`, error);
            
            if (attempt === 1) {
              // Wait 1 second before retry
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        }

        if (!result) {
          throw lastError || new Error("Failed to initiate call after retry attempts");
        }

        console.log("Call initiated:", result);
        setCallSuccess(true);

        setTimeout(() => {
          setIsCalling(false);
          setCallSuccess(false);
          setFormData({
            firstName: "",
            lastName: "",
            phone: "",
            email: "",
          });
          setShowForm(false); // Go back to the cards view
        }, 3000);
      } catch (error) {
        console.error("Error making call:", error);
        setIsCalling(false);
        
        // Show user-friendly error message instead of raw error
        const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
        let userMessage = "Unable to initiate the call at this time.";
        
        if (errorMessage.includes("Invalid or expired session")) {
          userMessage = "Your session has expired. Please refresh the page and try again.";
        } else if (errorMessage.includes("Invalid agent type")) {
          userMessage = "There was an issue with the agent selection. Please try again.";
        } else if (errorMessage.includes("VAPI_PHONE_NUMBER_ID")) {
          userMessage = "Service configuration error. Please contact support.";
        } else if (errorMessage.includes("Failed to initiate VAPI call")) {
          userMessage = "Unable to connect to the calling service. Please try again in a moment.";
        }
        
        alert(userMessage);
      }
    }, 1000);
  };

  const isFormValid =
    formData.firstName && formData.lastName && formData.phone && formData.email;

  const handleCardClick = (
    agentType: "recruiter" | "scheduling" | "payroll",
  ) => {
    setSelectedAgentType(agentType);
    setShowForm(true);
  };

  const handleBackToCards = () => {
    setShowForm(false);
  };

  return (
    <div className="min-h-screen w-full mx-auto max-w-[1366px]">
      {/* Header */}
      <div className="flex justify-center pt-8 pb-6 md:pt-16 md:pb-10">
        <img src={logo} alt="teambridge ai" className="h-16 md:h-24" />
      </div>

      {/* Main Content */}
      <div className="px-4 pb-12 md:px-8 lg:px-16 md:pb-24">
        {!isCalling ? (
          <>
            {!showForm ? (
              <>
                {/* Three Pillar Cards - Responsive for mobile and desktop */}
                <div className="flex flex-col md:flex-row justify-center items-center gap-8 md:gap-12 max-w-6xl mx-auto">
                  {/* Recruiter */}
                  <div className="flex flex-col items-center w-full max-w-[306px] md:w-[306px]">
                    <div className="relative w-full min-h-[480px] md:min-h-[580px] flex-shrink-0">
                      <div
                        className="absolute inset-0 rounded-[24px] pointer-events-none"
                        style={{
                          background:
                            "radial-gradient(120% 60% at 50% -20%, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.12) 42%, rgba(255,255,255,0.04) 55%, rgba(255,255,255,0) 60%), linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
                          padding: "1px",
                        }}
                      />
                      <button
                        type="button"
                        className="bg-[#010101]/50 rounded-[24px] pt-[36px] pr-[24px] pb-[36px] pl-[24px] cursor-pointer hover:bg-[#010101]/50 transition-all duration-300 w-full h-full text-left flex flex-col relative overflow-hidden gap-[48px] backdrop-blur-[20px] shadow-[0_4px_20px_0_rgba(0,0,0,0.25)]"
                        style={{
                          boxShadow:
                            "inset 0 0 0 1px rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.22), inset 0 -1px 0 rgba(255,255,255,0.04)",
                        }}
                        onClick={() => handleCardClick("recruiter")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleCardClick("recruiter");
                          }
                        }}
                        aria-label="Select Recruiter agent"
                      >
                        <div className="text-center flex flex-col items-center justify-center w-full">
                          <p className="text-xs text-slate-400 mb-1 text-center w-full">TINA</p>
                          <h3 className="text-3xl font-bold text-white text-center w-full">
                            Recruiter
                          </h3>
                        </div>

                        <div className="flex justify-center items-center w-full">
                          <div className="relative">
                            {/* Recruiter avatar with SVG files */}
                            <div className="relative w-24 h-24 flex items-center justify-center">
                              {/* Border SVG */}
                              <img
                                src={recruiterBorder}
                                alt=""
                                className="absolute inset-0 w-full h-full"
                              />
                              {/* Head SVG */}
                              <img
                                src={recruiterHead}
                                alt=""
                                className="relative z-10 w-20 h-20"
                              />
                            </div>
                            {/* Thumbs up icon overlay */}
                            <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-slate-800 rounded-full flex items-center justify-center border-2 border-slate-800">
                              <ThumbsUp className="w-4 h-4 text-white" />
                            </div>
                          </div>
                        </div>

                        <p className="text-white text-sm text-center leading-relaxed flex-grow">
                        Screen top talent, highlight best-fit opportunities, and 
                        motivate candidates to complete onboarding.
                        </p>

                        <div className="w-full">
                          <p className="text-xs text-slate-400 mb-3 text-center">
                            RESULTS
                          </p>
                          <div className="space-y-2 flex flex-col items-center">
                            {/* First result with diamond icon */}
                            <div className="bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 flex items-center gap-2 w-full max-w-[258px]">
                              <div className="w-1.5 h-1.5 bg-white transform rotate-45 flex-shrink-0"></div>
                              <span className="text-white text-sm">
                                100% candidate leads contacted
                              </span>
                            </div>
                            {/* Second result with diamond icon */}
                            <div className="bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 flex items-center gap-2 w-full max-w-[258px]">
                              <div className="w-1.5 h-1.5 bg-white transform rotate-45 flex-shrink-0"></div>
                              <span className="text-white text-sm">
                                10% increase in placements
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                    </div>
                    <div className="mt-4 md:mt-6">
                      <div
                        className={`inline-flex items-center gap-2 md:gap-3 rounded-full px-4 py-2 md:px-6 md:py-3 border text-white transition-all duration-300 ${
                          recruiterCount.isPulsing
                            ? "scale-105 shadow-lg shadow-white/30"
                            : ""
                        }`}
                        style={{
                          borderColor: recruiterCount.isPulsing
                            ? "rgba(255, 255, 255, 0.4)"
                            : "rgba(255,255,255,0.08)",
                          backdropFilter: "blur(10px)",
                          WebkitBackdropFilter: "blur(10px)",
                          background: recruiterCount.isPulsing
                            ? "radial-gradient(120% 80% at 85% 50%, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.1) 55%), rgba(255, 255, 255, 0.15)"
                            : "radial-gradient(120% 80% at 85% 50%, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 55%), rgba(255,255,255,0.08)",
                        }}
                      >
                        <img
                          src={callIcon}
                          alt=""
                          className="h-5 w-5 md:h-[22px] md:w-[22px]"
                        />
                        <span className="text-base md:text-[18px] leading-none">
                          {recruiterCount.count} conversations ongoing
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Scheduling */}
                  <div className="flex flex-col items-center w-full max-w-[306px] md:w-[306px]">
                    <div className="relative w-full min-h-[480px] md:min-h-[580px] flex-shrink-0">
                      <div
                        className="absolute inset-0 rounded-[24px] pointer-events-none"
                        style={{
                          background:
                            "radial-gradient(120% 60% at 50% -20%, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.12) 42%, rgba(255,255,255,0.04) 55%, rgba(255,255,255,0) 60%), linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
                          padding: "1px",
                        }}
                      />
                      <button
                        type="button"
                        className="bg-[#010101]/50 rounded-[24px] pt-[36px] pr-[24px] pb-[36px] pl-[24px] cursor-pointer hover:bg-[#010101]/50 transition-all duration-300 w-full h-full text-left flex flex-col relative overflow-hidden gap-[48px] backdrop-blur-[20px] shadow-[0_4px_20px_0_rgba(0,0,0,0.25)]"
                        style={{
                          boxShadow:
                            "inset 0 0 0 1px rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.22), inset 0 -1px 0 rgba(255,255,255,0.04)",
                        }}
                        onClick={() => handleCardClick("scheduling")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleCardClick("scheduling");
                          }
                        }}
                        aria-label="Select Jenny - Scheduling agent"
                      >
                        <div className="text-center flex flex-col items-center justify-center w-full">
                          <p className="text-xs text-slate-400 mb-1 text-center w-full">JENNY</p>
                          <h3 className="text-3xl font-bold text-white text-center w-full">
                            Scheduling
                          </h3>
                        </div>

                        <div className="flex justify-center items-center w-full">
                          <div className="relative">
                            {/* Scheduling avatar with SVG files */}
                            <div className="relative w-24 h-24 flex items-center justify-center">
                              {/* Border SVG */}
                              <img
                                src={schedulingBorder}
                                alt=""
                                className="absolute inset-0 w-full h-full"
                              />
                              {/* Head SVG */}
                              <img
                                src={schedulingHead}
                                alt=""
                                className="relative z-10 w-20 h-20"
                              />
                            </div>
                            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center border-2 border-slate-800">
                              <Calendar className="w-5 h-5 text-white" />
                            </div>
                          </div>
                        </div>

                        <p className="text-white text-sm text-center leading-relaxed flex-grow">
                        Instantly reach out to the best fit staff 
                        to eliminate the scramble of a last-minute call out
                        </p>

                        <div className="w-full">
                          <p className="text-xs text-slate-400 mb-3 text-center">
                            RESULTS
                          </p>
                          <div className="space-y-2 flex flex-col items-center">
                            <div className="bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 flex items-center gap-2 w-full max-w-[258px]">
                              <div className="w-1.5 h-1.5 bg-white transform rotate-45 flex-shrink-0"></div>
                              <span className="text-white text-sm">
                                Call outs replaced in minutes
                              </span>
                            </div>
                            <div className="bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 flex items-center gap-2 w-full max-w-[258px]">
                              <div className="w-1.5 h-1.5 bg-white transform rotate-45 flex-shrink-0"></div>
                              <span className="text-white text-sm">
                                20% increase in billable hours
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                    </div>
                    <div className="mt-4 md:mt-6">
                      <div
                        className={`inline-flex items-center gap-2 md:gap-3 rounded-full px-4 py-2 md:px-6 md:py-3 border text-white transition-all duration-300 ${
                          schedulingCount.isPulsing
                            ? "scale-105 shadow-lg shadow-white/30"
                            : ""
                        }`}
                        style={{
                          borderColor: schedulingCount.isPulsing
                            ? "rgba(255, 255, 255, 0.4)"
                            : "rgba(255,255,255,0.08)",
                          backdropFilter: "blur(10px)",
                          WebkitBackdropFilter: "blur(10px)",
                          background: schedulingCount.isPulsing
                            ? "radial-gradient(120% 80% at 85% 50%, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.1) 55%), rgba(255, 255, 255, 0.15)"
                            : "radial-gradient(120% 80% at 85% 50%, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 55%), rgba(255,255,255,0.08)",
                        }}
                      >
                        <img
                          src={callIcon}
                          alt=""
                          className="h-5 w-5 md:h-[22px] md:w-[22px]"
                        />
                        <span className="text-base md:text-[18px] leading-none">
                          {schedulingCount.count} conversations ongoing
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Payroll */}
                  <div className="flex flex-col items-center w-full max-w-[306px] md:w-[306px]">
                    <div className="relative w-full min-h-[480px] md:min-h-[580px] flex-shrink-0">
                      <div
                        className="absolute inset-0 rounded-[24px] pointer-events-none"
                        style={{
                          background:
                            "radial-gradient(120% 60% at 50% -20%, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.12) 42%, rgba(255,255,255,0.04) 55%, rgba(255,255,255,0) 60%), linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
                          padding: "1px",
                        }}
                      />
                      <button
                        type="button"
                        className="bg-[#010101]/50 rounded-[24px] pt-[36px] pr-[24px] pb-[36px] pl-[24px] cursor-pointer hover:bg-[#010101]/50 transition-all duration-300 w-full h-full text-left flex flex-col relative overflow-hidden gap-[48px] backdrop-blur-[20px] shadow-[0_4px_20px_0_rgba(0,0,0,0.25)]"
                        style={{
                          boxShadow:
                            "inset 0 0 0 1px rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.22), inset 0 -1px 0 rgba(255,255,255,0.04)",
                        }}
                        onClick={() => handleCardClick("payroll")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleCardClick("payroll");
                          }
                        }}
                        aria-label="Select John - Payroll agent"
                      >
                        <div className="text-center flex flex-col items-center justify-center w-full">
                          <p className="text-xs text-slate-400 mb-1 text-center w-full">JOHN</p>
                          <h3 className="text-3xl font-bold text-white text-center w-full">
                            Payroll
                          </h3>
                        </div>

                        <div className="flex justify-center items-center w-full">
                          <div className="relative">
                            {/* Payroll avatar with SVG files */}
                            <div className="relative w-24 h-24 flex items-center justify-center">
                              {/* Border SVG */}
                              <img
                                src={payrollBorder}
                                alt=""
                                className="absolute inset-0 w-full h-full"
                              />
                              {/* Head SVG */}
                              <img
                                src={payrollHead}
                                alt=""
                                className="relative z-10 w-20 h-20"
                              />
                            </div>
                            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center border-2 border-slate-800">
                              <Star className="w-5 h-5 text-white" />
                            </div>
                          </div>
                        </div>

                        <p className="text-white text-sm text-center leading-relaxed flex-grow">
                        Contact staff for missing time sheet information 
                        to ensure accurate payroll, every time
                        </p>

                        <div className="w-full">
                          <p className="text-xs text-slate-400 mb-3 text-center">
                            RESULTS
                          </p>
                          <div className="space-y-2 flex flex-col items-center">
                            <div className="bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 flex items-center gap-2 w-full max-w-[258px]">
                              <div className="w-1.5 h-1.5 bg-white transform rotate-45 flex-shrink-0"></div>
                              <span className="text-white text-sm">
                                80% reduction Payroll admin time
                              </span>
                            </div>
                            <div className="bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 flex items-center gap-2 w-full max-w-[258px]">
                              <div className="w-1.5 h-1.5 bg-white transform rotate-45 flex-shrink-0"></div>
                              <span className="text-white text-sm">
                                100% Accurate Invoicing
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                    </div>
                    <div className="mt-4 md:mt-6">
                      <div
                        className={`inline-flex items-center gap-2 md:gap-3 rounded-full px-4 py-2 md:px-6 md:py-3 border text-white transition-all duration-300 ${
                          payrollCount.isPulsing
                            ? "scale-105 shadow-lg shadow-white/30"
                            : ""
                        }`}
                        style={{
                          borderColor: payrollCount.isPulsing
                            ? "rgba(255, 255, 255, 0.4)"
                            : "rgba(255,255,255,0.08)",
                          backdropFilter: "blur(10px)",
                          WebkitBackdropFilter: "blur(10px)",
                          background: payrollCount.isPulsing
                            ? "radial-gradient(120% 80% at 85% 50%, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.1) 55%), rgba(255, 255, 255, 0.15)"
                            : "radial-gradient(120% 80% at 85% 50%, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 55%), rgba(255,255,255,0.08)",
                        }}
                      >
                        <img
                          src={callIcon}
                          alt=""
                          className="h-5 w-5 md:h-[22px] md:w-[22px]"
                        />
                        <span className="text-base md:text-[18px] leading-none">
                          {payrollCount.count} conversations ongoing
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* Form View - Matching the second image exactly */
              <div className="max-w-md mx-auto">
                <Card className="bg-slate-800/90 border border-slate-700 rounded-2xl">
                  <CardContent className="p-8">
                    {selectedAgentType && (
                      <div className="mb-6 p-3 bg-slate-700/50 rounded-lg border border-slate-600">
                        <p className="text-sm text-slate-300 mb-1">
                          Selected Specialist
                        </p>
                        <p className="text-lg font-semibold text-white capitalize">
                          {selectedAgentType === "recruiter" &&
                            "Tina - Recruiter"}
                          {selectedAgentType === "scheduling" &&
                            "Jenny - Scheduling"}
                          {selectedAgentType === "payroll" && "John - Payroll"}
                        </p>
                      </div>
                    )}
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="space-y-2">
                        <Label
                          htmlFor={firstNameId}
                          className="text-sm font-medium text-white"
                        >
                          First Name
                        </Label>
                        <Input
                          id={firstNameId}
                          name="firstName"
                          placeholder="John"
                          value={formData.firstName}
                          onChange={handleChange}
                          disabled={isLoading}
                          className="h-12 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-blue-500"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor={lastNameId}
                          className="text-sm font-medium text-white"
                        >
                          Last Name
                        </Label>
                        <Input
                          id={lastNameId}
                          name="lastName"
                          placeholder="Doe"
                          value={formData.lastName}
                          onChange={handleChange}
                          disabled={isLoading}
                          className="h-12 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-blue-500"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor={phoneId}
                          className="text-sm font-medium text-white"
                        >
                          Phone
                        </Label>
                        <Input
                          id={phoneId}
                          name="phone"
                          type="tel"
                          placeholder="(XXX)-XXX-XXXX"
                          value={formData.phone}
                          onChange={handleChange}
                          disabled={isLoading}
                          className="h-12 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-blue-500"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor={emailId}
                          className="text-sm font-medium text-white"
                        >
                          Email
                        </Label>
                        <Input
                          id={emailId}
                          name="email"
                          type="email"
                          placeholder="example@mail.com"
                          value={formData.email}
                          onChange={handleChange}
                          disabled={isLoading}
                          className="h-12 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-blue-500"
                        />
                      </div>

                      <div className="pt-4">
                        <Button
                          type="submit"
                          className={`w-full h-12 text-base font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                            isFormValid
                              ? "bg-gradient-to-r from-[#1EDFDE] to-[#8C4FE2] text-white hover:opacity-90"
                              : "bg-slate-200 text-slate-800 hover:bg-slate-100"
                          }`}
                          disabled={isLoading || !isFormValid}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <span className="flex items-center gap-2">
                              Activate Agent
                              <div
                                className={`w-2 h-2 border-r-2 border-b-2 border-l-0 border-t-0 transform -rotate-45 ${
                                  isFormValid
                                    ? "border-white"
                                    : "border-slate-800"
                                }`}
                              ></div>
                            </span>
                          )}
                        </Button>
                      </div>
                    </form>

                    <div className="mt-6 text-center">
                      <button
                        type="button"
                        onClick={handleBackToCards}
                        className="text-slate-400 hover:text-white text-sm transition-colors"
                      >
                        ← Back to agents
                      </button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="relative mb-8">
              <div className="absolute inset-0 bg-blue-500 rounded-full animate-ping"></div>
              <div className="relative bg-gradient-to-br from-blue-600 to-purple-600 rounded-full p-12">
                {callSuccess ? (
                  <CheckCircle className="h-24 w-24 text-white" />
                ) : (
                  <Phone className="h-24 w-24 text-white animate-bounce" />
                )}
              </div>
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">
              {callSuccess ? "Agent Activated!" : "Activating Agent..."}
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              {callSuccess
                ? "Successfully Connected!"
                : "Connecting to Agent..."}
            </p>
            <div className="flex space-x-2">
              <div
                className={`h-2 w-2 bg-blue-600 rounded-full animate-bounce ${!callSuccess ? "[animation-delay:-0.3s]" : ""}`}
              ></div>
              <div
                className={`h-2 w-2 bg-blue-600 rounded-full animate-bounce ${!callSuccess ? "[animation-delay:-0.15s]" : ""}`}
              ></div>
              <div
                className={`h-2 w-2 bg-blue-600 rounded-full animate-bounce ${!callSuccess ? "" : ""}`}
              ></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
